'use client';

// The browser-mockup frame (.browser_component / .browser_window) and the
// carousel arrows (.testimonials2_slider-arrow) this component renders are
// defined in home.css. It is used on /admissions-consulting/* (which does not
// otherwise load home.css), so co-locate the import to keep the carousel styled
// — without it the arrows rendered unstyled and failed the target-size audit.
import '@/styles/home.css';
import Image from 'next/image';
import { useState, useCallback, useEffect, useRef } from 'react';

const reviewImages = [
    { src: '/reviews/GradBrew_testimonial_1.webp', alt: 'GradBrew student review 1' },
    { src: '/reviews/GradBrew_testimonial_2.webp', alt: 'GradBrew student review 2' },
    { src: '/reviews/GradBrew_testimonial_3.webp', alt: 'GradBrew student review 3' },
    { src: '/reviews/GradBrew_testimonial_4.webp', alt: 'GradBrew student review 4' },
    { src: '/reviews/GradBrew_testimonial_5.webp', alt: 'GradBrew student review 5' },
    { src: '/reviews/GradBrew_testimonial_6.webp', alt: 'GradBrew student review 6' },
    { src: '/reviews/GradBrew_testimonial_7.webp', alt: 'GradBrew student review 7' },
    { src: '/reviews/GradBrew_testimonial_8.webp', alt: 'GradBrew student review 8' },
    { src: '/reviews/GradBrew_testimonial_9.webp', alt: 'GradBrew student review 9' },
    { src: '/reviews/GradBrew_testimonial_10.webp', alt: 'GradBrew student review 10' },
    { src: '/reviews/GradBrew_testimonial_11.webp', alt: 'GradBrew student review 11' },
];

const total = reviewImages.length;

function wrap(i: number) {
    return ((i % total) + total) % total;
}

export function IndustryTestimonialCarousel({ autoPlayInterval = 4000 }: { autoPlayInterval?: number }) {
    const [current, setCurrent] = useState(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const next = useCallback(() => setCurrent((c) => wrap(c + 1)), []);
    const prev = useCallback(() => setCurrent((c) => wrap(c - 1)), []);

    useEffect(() => {
        timerRef.current = setTimeout(next, autoPlayInterval);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [current, next, autoPlayInterval]);

    const prevImg  = reviewImages[wrap(current - 1)]!;
    const currImg  = reviewImages[current]!;
    const nextImg  = reviewImages[wrap(current + 1)]!;

    return (
        <div className="itc_wrapper">
            {/* Left peek card (T1) */}
            <button
                type="button"
                aria-label="Previous review"
                className="itc_side-card itc_side-card--left"
                onClick={prev}
            >
                <Image
                    src={prevImg.src}
                    alt={prevImg.alt}
                    width={1500}
                    height={1500}
                    sizes="(max-width: 767px) 30vw, 220px"
                    className="itc_side-image"
                    loading="lazy"
                />
            </button>

            {/* Center card (T2) – browser frame */}
            <div className="itc_main-card">
                <div className="browser_component">
                    <div className="browser_top-bar">
                        <div className="browser_controls">
                            <div className="browser_control"></div>
                            <div className="browser_control"></div>
                            <div className="browser_control"></div>
                        </div>
                        <div className="itc_nav-buttons">
                            <button
                                type="button"
                                onClick={prev}
                                aria-label="Previous review"
                                className="testimonials2_slider-arrow is-bottom-previous"
                            >
                                <span className="testimonials2_slider-arrow-icon w-embed">
                                    <svg role="img" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                        <title>Back</title>
                                        <path fill="currentColor" d="M7,9h8c0.6,0,1-0.4,1-1s-0.4-1-1-1H7V2L0,8l7,6V9z" />
                                    </svg>
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                aria-label="Next review"
                                className="testimonials2_slider-arrow is-bottom-next"
                            >
                                <span className="testimonials2_slider-arrow-icon w-embed">
                                    <svg role="img" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                        <title>Next</title>
                                        <path fill="currentColor" d="M9,7H1C0.4,7,0,7.4,0,8s0.4,1,1,1h8v5l7-6L9,2V7z" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                    <div className="browser_window itc_browser-window">
                        <Image
                            src={currImg.src}
                            alt={currImg.alt}
                            width={1500}
                            height={1500}
                            sizes="(max-width: 1279px) 100vw, 800px"
                            className="itc_main-image"
                            priority
                            fetchPriority="high"
                        />
                    </div>
                </div>
            </div>

            {/* Right peek card (T3) */}
            <button
                type="button"
                aria-label="Next review"
                className="itc_side-card itc_side-card--right"
                onClick={next}
            >
                <Image
                    src={nextImg.src}
                    alt={nextImg.alt}
                    width={1500}
                    height={1500}
                    sizes="(max-width: 767px) 30vw, 220px"
                    className="itc_side-image"
                    loading="lazy"
                />
            </button>
        </div>
    );
}
